import Link from 'next/link'
import { useRouter } from 'next/router'

import type { FC, FormEvent, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import type { SearchHit } from '@/types'

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowRight, LoaderCircle, Search, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { SearchHitRow } from '@/components/content/search-hit'

import {
    SEARCH_DEBOUNCE_MS,
    SEARCH_MAX_TERM_LENGTH,
    SEARCH_MIN_LENGTH
} from '@/constants/search'

type Status = 'idle' | 'loading' | 'done' | 'error'

interface SiteSearchProps {
    className?: string
}

// Header search. Opens an overlay rather than routing, so a visitor can look
// something up without losing the page they are on, and falls through to the
// full /search page for anything the preview list truncates.
export const SiteSearch: FC<SiteSearchProps> = ({ className }): ReactNode => {
    const router = useRouter()

    const [open, setOpen] = useState(false)
    const [term, setTerm] = useState('')
    const [hits, setHits] = useState<SearchHit[]>([])
    const [status, setStatus] = useState<Status>('idle')
    const [active, setActive] = useState(0)
    const [shortcut, setShortcut] = useState<string | null>(null)
    const [mounted, setMounted] = useState(false)

    const inputRef = useRef<HTMLInputElement>(null)
    const trimmed = term.trim()
    const isSearchable = trimmed.length >= SEARCH_MIN_LENGTH

    // Runs only after mount: the shortcut label depends on the visitor's
    // platform, which the server cannot know without a hydration mismatch, and
    // the portal needs `document`.
    useEffect(() => {
        const isApple = /mac|iphone|ipad|ipod/i.test(window.navigator.platform)
        setShortcut(isApple ? '⌘K' : 'Ctrl K')
        setMounted(true)
    }, [])

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault()
                setOpen((value) => !value)
            } else if (event.key === 'Escape') {
                setOpen(false)
            }
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [])

    useEffect(() => {
        setOpen(false)
    }, [router.asPath])

    useEffect(() => {
        if (!open) {
            setTerm('')
            setHits([])
            setStatus('idle')
            setActive(0)
            return
        }

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        inputRef.current?.focus()

        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [open])

    // Debounced, and every in-flight request is aborted by the next keystroke so
    // a slow response can never overwrite the results of a newer one.
    useEffect(() => {
        if (!open) return

        if (!isSearchable) {
            setHits([])
            setStatus('idle')
            return
        }

        setStatus('loading')
        const controller = new AbortController()

        const timer = window.setTimeout(() => {
            fetch(`/api/search?q=${encodeURIComponent(trimmed)}`, { signal: controller.signal })
                .then((response) => response.json())
                .then((data: { ok: boolean; hits?: SearchHit[] }) => {
                    if (!data.ok) throw new Error('search failed')
                    setHits(data.hits ?? [])
                    setActive(0)
                    setStatus('done')
                })
                .catch((error: unknown) => {
                    if (error instanceof DOMException && error.name === 'AbortError') return
                    setHits([])
                    setStatus('error')
                })
        }, SEARCH_DEBOUNCE_MS)

        return () => {
            controller.abort()
            window.clearTimeout(timer)
        }
    }, [open, trimmed, isSearchable])

    const close = useCallback(() => setOpen(false), [])

    const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
        if (hits.length === 0) return

        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActive((value) => (value + 1) % hits.length)
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActive((value) => (value - 1 + hits.length) % hits.length)
        }
    }

    const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const hit = hits[active]
        if (hit) {
            void router.push(hit.href)
            return
        }

        if (isSearchable) void router.push(`/search?q=${encodeURIComponent(trimmed)}`)
    }

    // Portalled to the body on purpose. The header sets `backdrop-blur`, and a
    // backdrop-filter makes an element the containing block for its fixed-position
    // descendants, which would otherwise trap this overlay inside the header bar.
    const overlay = (
        <div className='fixed inset-0 z-[60] flex justify-center px-4 pb-6 pt-[8vh] sm:pt-[12vh]'>
            <button
                type='button'
                aria-label='Close search'
                onClick={close}
                className='absolute inset-0 cursor-default bg-foreground/25 backdrop-blur-sm'
            />

            <div
                role='dialog'
                aria-modal='true'
                aria-label='Search guides and articles'
                className='relative flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl'
            >
                <form
                    onSubmit={onSubmit}
                    className='flex shrink-0 items-center gap-3 border-b border-border px-5'
                >
                    <Search className='size-5 shrink-0 text-muted-foreground' />

                    <input
                        ref={inputRef}
                        type='search'
                        value={term}
                        onChange={(event) => setTerm(event.target.value)}
                        onKeyDown={onKeyDown}
                        maxLength={SEARCH_MAX_TERM_LENGTH}
                        placeholder='Search guides and articles'
                        aria-label='Search guides and articles'
                        autoComplete='off'
                        className='h-14 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground'
                    />

                    <button
                        type='button'
                        onClick={close}
                        aria-label='Close search'
                        className='inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground'
                    >
                        <X className='size-4' />
                    </button>
                </form>

                <div className='min-h-0 flex-1 overflow-y-auto p-2'>
                    {!isSearchable && (
                        <p className='px-3 py-6 text-sm text-muted-foreground'>
                            Type at least {SEARCH_MIN_LENGTH} characters to search every guide and
                            article.
                        </p>
                    )}

                    {isSearchable && status === 'loading' && (
                        <p className='flex items-center gap-2 px-3 py-6 text-sm text-muted-foreground'>
                            <LoaderCircle className='size-4 animate-spin' />
                            Searching
                        </p>
                    )}

                    {isSearchable && status === 'error' && (
                        <p className='px-3 py-6 text-sm text-muted-foreground'>
                            Search is unavailable right now. Please try again in a moment.
                        </p>
                    )}

                    {isSearchable && status === 'done' && hits.length === 0 && (
                        <div className='px-3 py-6'>
                            <p className='text-sm text-foreground'>
                                No results for &ldquo;{trimmed}&rdquo;.
                            </p>
                            <p className='mt-1 text-sm text-muted-foreground'>
                                Try a broader word, or browse the ten guides from the header.
                            </p>
                        </div>
                    )}

                    {isSearchable && status === 'done' && hits.length > 0 && (
                        <>
                            <p aria-live='polite' className='sr-only'>
                                {hits.length} {hits.length === 1 ? 'result' : 'results'} for{' '}
                                {trimmed}
                            </p>

                            <ul className='flex flex-col gap-0.5'>
                                {hits.map((hit, index) => (
                                    <li key={`${hit.kind}-${hit.id}`}>
                                        <SearchHitRow
                                            hit={hit}
                                            active={index === active}
                                            onMouseEnter={() => setActive(index)}
                                            onClick={close}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>

                {isSearchable && status === 'done' && hits.length > 0 && (
                    <Link
                        href={`/search?q=${encodeURIComponent(trimmed)}`}
                        onClick={close}
                        className='flex shrink-0 items-center justify-between border-t border-border px-5 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary'
                    >
                        See all results for &ldquo;{trimmed}&rdquo;
                        <ArrowRight className='size-4' />
                    </Link>
                )}
            </div>
        </div>
    )

    return (
        <>
            <button
                type='button'
                onClick={() => setOpen(true)}
                aria-label='Search guides and articles'
                className={cn(
                    'inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground lg:w-44 xl:w-52',
                    className
                )}
            >
                <Search className='size-4 shrink-0' />
                <span className='hidden text-sm lg:inline'>Search</span>
                {shortcut && (
                    <kbd className='ml-auto hidden rounded border border-border bg-secondary px-1.5 py-0.5 font-sans text-[0.65rem] font-medium text-muted-foreground lg:inline'>
                        {shortcut}
                    </kbd>
                )}
            </button>

            {mounted && open && createPortal(overlay, document.body)}
        </>
    )
}
